import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LandingNav from '@/components/landing/LandingNav';
import LandingHero from '@/components/landing/LandingHero';
import HowItWorks from '@/components/landing/HowItWorks';
import WhoItsFor from '@/components/landing/WhoItsFor';
import LandingFooter from '@/components/landing/LandingFooter';

/**
 * LandingPage — unauthenticated marketing page served at `/`.
 *
 * Auth-aware routing:
 * - Authenticated users are redirected straight to the dashboard (no flash).
 * - While auth state is loading we render nothing to avoid flicker.
 * - Guests see the full marketing page.
 *
 * This component makes NO API calls and has no dependency on user data.
 */
export default function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    document.title = 'PointeTracer — Movement analysis, measured';
  }, []);

  // While session validation is in-flight, render nothing.
  // The auth check is fast (one token read + one API call); avoiding
  // a full spinner here prevents a flicker between landing and dashboard.
  if (isLoading) {
    return null;
  }

  // Authenticated users go straight to the app (templates is the natural home
  // while full dashboard polish is in a later sprint).
  if (isAuthenticated) {
    return <Navigate to="/templates" replace />;
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: 'var(--color-surface-base)',
        color: 'var(--color-text-primary)',
        scrollBehavior: 'smooth',
      }}
    >
      <LandingNav />

      <main>
        <LandingHero />

        {/* Divider */}
        <div
          aria-hidden
          className="mx-auto max-w-7xl px-6 lg:px-8"
        >
          <hr style={{ borderColor: 'var(--color-border-subtle)' }} />
        </div>

        <HowItWorks />

        {/* Divider */}
        <div
          aria-hidden
          className="mx-auto max-w-7xl px-6 lg:px-8"
        >
          <hr style={{ borderColor: 'var(--color-border-subtle)' }} />
        </div>

        <WhoItsFor />

        {/* Demo video slot — placeholder for future video */}
        <section className="py-20 md:py-24">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div className="text-center mb-8">
              <p
                className="mb-3 text-xs font-medium uppercase tracking-widest"
                style={{ color: 'var(--color-brand-primary)', letterSpacing: '0.12em' }}
              >
                See it in action
              </p>
              <h2 className="text-h1" style={{ color: 'var(--color-text-primary)' }}>
                A full evaluation in 90 seconds.
              </h2>
            </div>

            {/* 16:9 video placeholder card */}
            <div
              className="relative w-full overflow-hidden rounded-2xl"
              style={{
                paddingTop: '56.25%', /* 16:9 aspect ratio */
                background: 'var(--color-surface-raised-2)',
                border: '1px solid var(--color-border-subtle)',
              }}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                {/* Play icon placeholder */}
                <div
                  className="flex h-16 w-16 items-center justify-center rounded-full"
                  style={{
                    background: 'var(--color-brand-glow)',
                    border: '2px solid var(--color-brand-primary)',
                  }}
                >
                  <svg
                    className="ml-1 h-7 w-7"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                    style={{ color: 'var(--color-brand-primary)' }}
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
                <p
                  className="text-body-sm"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Demo video coming soon
                </p>
              </div>
            </div>

            <p
              className="mt-4 text-center text-body-sm"
              style={{ color: 'var(--color-text-muted)' }}
            >
              See a full evaluation in 90 seconds.
            </p>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
